/* Team identity for a pick row: primary color, city/region, and nickname.
 *
 * No logos anywhere in this app — team marks are trademarked and a pick'em pool
 * is exactly the commercial-adjacent use that gets letters sent. A color bar
 * plus the team's name in plain type identifies a team without reproducing
 * anything owned. Names themselves are nominative use: naming the team you are
 * picking is unavoidable and not what trademark protects against.
 *
 * Keyed by the canonical abbreviations actually stored in `games.home_team` /
 * `games.away_team` — the nflverse set from scripts/teams.mjs (LA, WAS, LV,
 * JAX), not ESPN's (LAR, WSH, LVR, JAC). Keying this map off ESPN's spellings
 * would silently fall through to the default for four teams.
 *
 * `city` is city or region, whichever the team actually goes by: Arizona and
 * Carolina and New England are not cities, and "Phoenix Cardinals" is wrong.
 * Four teams share a city with a division rival (LA/LAC, NYG/NYJ), so the
 * nickname is what disambiguates — which is why it gets the visual weight.
 *
 * Two deliberate departures from the official "primary" color:
 *
 * 1. Teams whose primary is effectively black or near-black navy (PIT, LV, NO,
 *    CLE, CHI) use their iconic secondary instead. At 4px on white, five teams
 *    rendering as indistinguishable dark slivers defeats the point of the bar,
 *    and near-navy also reads as app chrome rather than team identity.
 * 2. TEN uses its lighter blue for the same reason.
 *
 * Known collisions, left alone because they only surface if the two teams play
 * each other: CIN and DEN share #FB4F14, NE and SEA share #002244. Seattle's
 * action green is the obvious fix for the latter and is deliberately NOT used —
 * green means *picked* in this app, and a green team bar would read as state.
 */

interface Team {
  color: string
  city: string
  nickname: string
}

const TEAMS: Record<string, Team> = {
  ARI: { color: '#97233F', city: 'Arizona', nickname: 'Cardinals' },
  ATL: { color: '#A71930', city: 'Atlanta', nickname: 'Falcons' },
  BAL: { color: '#241773', city: 'Baltimore', nickname: 'Ravens' },
  BUF: { color: '#00338D', city: 'Buffalo', nickname: 'Bills' },
  CAR: { color: '#0085CA', city: 'Carolina', nickname: 'Panthers' },
  // secondary orange; primary #0B162A is navy chrome
  CHI: { color: '#C83803', city: 'Chicago', nickname: 'Bears' },
  CIN: { color: '#FB4F14', city: 'Cincinnati', nickname: 'Bengals' },
  // secondary orange; primary #311D00 is near-black
  CLE: { color: '#FF3C00', city: 'Cleveland', nickname: 'Browns' },
  DAL: { color: '#041E42', city: 'Dallas', nickname: 'Cowboys' },
  DEN: { color: '#FB4F14', city: 'Denver', nickname: 'Broncos' },
  DET: { color: '#0076B6', city: 'Detroit', nickname: 'Lions' },
  GB: { color: '#203731', city: 'Green Bay', nickname: 'Packers' },
  HOU: { color: '#03202F', city: 'Houston', nickname: 'Texans' },
  IND: { color: '#002C5F', city: 'Indianapolis', nickname: 'Colts' },
  JAX: { color: '#006778', city: 'Jacksonville', nickname: 'Jaguars' },
  KC: { color: '#E31837', city: 'Kansas City', nickname: 'Chiefs' },
  LA: { color: '#003594', city: 'Los Angeles', nickname: 'Rams' },
  LAC: { color: '#0080C6', city: 'Los Angeles', nickname: 'Chargers' },
  // silver; primary is black
  LV: { color: '#A5ACAF', city: 'Las Vegas', nickname: 'Raiders' },
  MIA: { color: '#008E97', city: 'Miami', nickname: 'Dolphins' },
  MIN: { color: '#4F2683', city: 'Minnesota', nickname: 'Vikings' },
  NE: { color: '#002244', city: 'New England', nickname: 'Patriots' },
  // gold; primary is black
  NO: { color: '#D3BC8D', city: 'New Orleans', nickname: 'Saints' },
  NYG: { color: '#0B2265', city: 'New York', nickname: 'Giants' },
  NYJ: { color: '#125740', city: 'New York', nickname: 'Jets' },
  PHI: { color: '#004C54', city: 'Philadelphia', nickname: 'Eagles' },
  // gold; primary is black
  PIT: { color: '#FFB612', city: 'Pittsburgh', nickname: 'Steelers' },
  SEA: { color: '#002244', city: 'Seattle', nickname: 'Seahawks' },
  SF: { color: '#AA0000', city: 'San Francisco', nickname: '49ers' },
  TB: { color: '#D50A0A', city: 'Tampa Bay', nickname: 'Buccaneers' },
  // lighter blue; primary #0C2340 is navy chrome
  TEN: { color: '#4B92DB', city: 'Tennessee', nickname: 'Titans' },
  WAS: { color: '#5A1414', city: 'Washington', nickname: 'Commanders' },
}

/* Unknown abbreviations degrade rather than throw. Ingestion already refuses to
   write a team it cannot normalize (scripts/teams.mjs), so a miss here means a
   new franchise or a relocation — falling back to a grey bar and the raw
   abbreviation is a better failure than a page that does not render. The empty
   city is what the caller keys off to drop the top line entirely. */
function lookup(abbreviation: string): Team {
  return TEAMS[abbreviation] ?? { color: '#D8DAD3', city: '', nickname: abbreviation }
}

export function teamColor(abbreviation: string): string {
  return lookup(abbreviation).color
}

export function teamLabel(abbreviation: string): { city: string; nickname: string } {
  const { city, nickname } = lookup(abbreviation)
  return { city, nickname }
}
